/**
 * Yakıt Yönetimi Y4 metinleri: ROB Mutabakatı (ortağın 06-rob-mutabakati.jpeg), Voyage Analizi
 * (07-voyage-analizi.jpeg), Genel Bakış'ın ROB mutabakatı kartı ve raporu (01). Anahtarlar API
 * değerleriyle (yöntem, kaynak, neden, blokaj, eksik veri kodu, hata kodu) eşleşir.
 */

export const fuelRobEn = {
  title: 'ROB Reconciliation',
  sounding: 'Sounding',
  soundingOption: '{{no}} · {{at}}',
  newSounding: 'New Measurement',
  noSounding: 'No ROB measurement yet',
  noSoundingBody:
    'Enter a tank sounding to compare with the calculated ROB. A measurement never changes the stock balance.',
  info: 'ROB Measurement Information',
  measuredAt: 'Measurement Date / Time',
  sourceKind: 'Source Report',
  sourceReference: 'Report Reference',
  measuredBy: 'Measured By',
  basis: 'Conversion Basis',
  basisHint:
    'Required for manual MT and for soundings of tanks without a calibration table (e.g. survey report no.).',
  sources: {
    ullage_report: 'Ullage Report (On Board)',
    sounding_report: 'Sounding Report (On Board)',
    bunker_survey: 'Bunker Survey',
    voyage_report: 'Voyage Report',
    other: 'Other',
  },
  tanks: 'Tank Measurements',
  columns: {
    tank: 'Tank',
    method: 'Method',
    reading: 'Sounding (cm)',
    temp: 'Temperature (°C)',
    volume: 'Volume (m³)',
    density: 'Density (MT/m³)',
    measured: 'Measured MT',
  },
  methods: { sounding: 'Sounding', ullage: 'Ullage', sensor: 'Sensor', manual: 'Manual MT' },
  conversion:
    'VCF {{vcf}} (ASTM D1250 Table 54B) · volume at 15 °C {{v15}} m³ · calibration v{{version}}',
  conversionNoTable: 'VCF {{vcf}} (ASTM D1250 Table 54B) · volume at 15 °C {{v15}} m³',
  noTanks: 'No active tanks.',
  byFuel: 'ROB Comparison (By Fuel)',
  compareColumns: {
    fuel: 'Fuel',
    calculated: 'Calculated ROB (MT)',
    measured: 'Measured ROB (MT)',
    diff: 'Difference (MT)',
  },
  total: 'Grand Total (All Fuels)',
  calculated: 'Calculated ROB',
  measured: 'Measured ROB',
  difference: 'Difference',
  previewNote: 'Preview against the current ledger; saved measurements compare at their own time.',
  thresholdUndefined:
    'No reconciliation threshold is defined for this method: the office sets it (no built-in tolerance).',
  thresholdExceeded: 'The difference exceeds the company threshold for this method.',
  unmeasured: 'Not measured: {{tanks}} (calculated ROB not compared).',
  adjustment: 'Reconciliation and Adjustment',
  reason: 'Reason for Difference',
  reasons: {
    measurement_tolerance: 'Measurement Tolerance (Normal)',
    measurement_error: 'Measurement Error',
    calibration: 'Calibration Table',
    density: 'Density Difference',
    temperature: 'Temperature Correction',
    unrecorded_consumption: 'Unrecorded Consumption',
    sludge_water: 'Sludge / Water Drain',
    bunker_difference: 'Bunker Delivery Difference',
    other: 'Other',
  },
  description: 'Description',
  adjustmentMt: 'Adjustment Quantity (MT)',
  document: 'Supporting Document',
  byTank: 'Adjustment by tank',
  useDifferences: 'Use differences',
  tankDiff: 'Difference (MT)',
  tankAdjustment: 'Adjustment (MT)',
  note: 'Note:',
  noteBody:
    'A measurement does not change the stock balance. If there is a difference, raise an adjustment request; it reaches the ledger only after approval on board and by the office.',
  save: 'Save Measurement',
  saved: 'Measurement saved. The stock balance did not change.',
  remeasure: 'Re-measure',
  request: 'Request Adjustment',
  requested: 'Adjustment request saved as draft.',
  submit: 'Submit for Approval',
  adjustmentRecord: 'Adjustment request {{no}}',
  officeStep: 'Awaiting the office approval (company approver).',
  appliedOnBoard:
    'Approved. The ship server writes the adjustment to the ledger after synchronisation.',
  posted: 'Adjustment posted to the ledger: {{mt}} MT.',
  approveOffice: 'Approve',
  returnOffice: 'Return',
  officeReadOnly:
    'Measurements and adjustment requests are vessel data. The office decides the office step of an adjustment and sets the reconciliation thresholds.',
  thresholds: 'Reconciliation Thresholds',
  thresholdsTitle: 'Reconciliation thresholds and cost method',
  thresholdsHint:
    'Company parameter per measurement method: a difference above the MT or % limit is flagged. Empty means no threshold (moves to the MRV parameter sets in a later stage).',
  thresholdMt: '{{method}} limit (MT)',
  thresholdPct: '{{method}} limit (%)',
  costMethod: 'Cost method',
  costMethods: { weighted_average: 'Weighted Average', batch: 'Batch (BDN)' },
  settingsSaved: 'Thresholds saved.',
  cancel: 'Cancel',
  saveSettings: 'Save',
  status: { within: 'Within threshold', exceeds: 'Exceeds threshold', undefined: 'No threshold' },
  month: {
    title: 'ROB Month Close',
    month: 'Month (UTC)',
    closed: 'Closed on {{at}} by {{by}}.',
    open: 'Open',
    notEnded: 'The month has not ended yet.',
    noBlockers: 'No blocking issue: the month can be closed.',
    close: 'Close Month',
    closedDone: 'Month {{month}} closed.',
    remarks: 'Remarks',
    lockNote: 'After the month is closed on board, the office locks the period.',
    blockers: {
      NEGATIVE_ROB: 'Negative ROB in {{tank}} ({{at}})',
      CAPACITY_EXCEEDED: 'Capacity exceeded in {{tank}} ({{at}})',
      MISSING_CONVERSION_BASIS: 'Measurement without conversion basis: {{tank}} ({{at}})',
      UNAPPROVED_ADJUSTMENT: 'Adjustment request not approved: {{no}}',
      UNMATCHED_CONSUMPTION: 'Consumption without voyage ({{at}})',
    },
  },
  report: {
    title: 'ROB Reconciliation Report',
    vessel: 'Vessel',
    voyage: 'Voyage',
    period: 'Period',
    printed: 'Printed',
    product: 'Product',
    opening: 'Opening ROB (MT)',
    bunkered: 'Total Bunkered (MT)',
    consumed: 'Total Consumed (MT)',
    calculated: 'Calculated ROB (MT)',
    measured: 'Ullage ROB (MT)',
    diff: 'Difference (MT)',
    sounding: 'Latest measurement {{no}} · {{at}}',
    tankTitle: 'Tank measurements',
    noSounding: 'No measurement in the period: measured ROB is empty.',
    footer: 'Felixmate · derived from approved ledger movements',
  },
};

export const fuelRobTr: typeof fuelRobEn = {
  title: 'ROB Mutabakatı',
  sounding: 'Ölçüm',
  soundingOption: '{{no}} · {{at}}',
  newSounding: 'Yeni Ölçüm',
  noSounding: 'Henüz ROB ölçümü yok',
  noSoundingBody:
    'Hesaplanan ROB ile karşılaştırmak için tank ölçümü girin. Ölçüm stok bakiyesini hiçbir zaman değiştirmez.',
  info: 'ROB Ölçüm Bilgileri',
  measuredAt: 'Ölçüm Tarih / Saat',
  sourceKind: 'Kaynak Rapor',
  sourceReference: 'Rapor Referansı',
  measuredBy: 'Ölçen Kişi',
  basis: 'Dönüşüm Dayanağı',
  basisHint:
    'Elle MT ve kalibrasyon tablosu olmayan tankın ölçümünde zorunlu (örn. sörvey raporu no).',
  sources: {
    ullage_report: 'Ullage Raporu (Gemide)',
    sounding_report: 'Sounding Raporu (Gemide)',
    bunker_survey: 'Bunker Sörveyi',
    voyage_report: 'Sefer Raporu',
    other: 'Diğer',
  },
  tanks: 'Tank Ölçümleri',
  columns: {
    tank: 'Tank',
    method: 'Yöntem',
    reading: 'Sounding (cm)',
    temp: 'Sıcaklık (°C)',
    volume: 'Hacim (m³)',
    density: 'Yoğunluk (MT/m³)',
    measured: 'Ölçülen MT',
  },
  methods: { sounding: 'Sounding', ullage: 'Ullage', sensor: 'Sensör', manual: 'Elle MT' },
  conversion:
    'VCF {{vcf}} (ASTM D1250 Tablo 54B) · 15 °C hacmi {{v15}} m³ · kalibrasyon s{{version}}',
  conversionNoTable: 'VCF {{vcf}} (ASTM D1250 Tablo 54B) · 15 °C hacmi {{v15}} m³',
  noTanks: 'Aktif tank yok.',
  byFuel: 'ROB Karşılaştırması (Yakıt Bazında)',
  compareColumns: {
    fuel: 'Yakıt',
    calculated: 'Hesaplanan ROB (MT)',
    measured: 'Ölçülen ROB (MT)',
    diff: 'Fark (MT)',
  },
  total: 'Genel Toplam (Tüm Yakıtlar)',
  calculated: 'Hesaplanan ROB',
  measured: 'Ölçülen ROB',
  difference: 'Fark',
  previewNote:
    'Güncel deftere göre önizleme; kaydedilen ölçüm kendi anındaki defterle karşılaştırılır.',
  thresholdUndefined:
    'Bu yöntem için mutabakat eşiği tanımlı değil: ofis belirler (yazılımda gömülü tolerans yok).',
  thresholdExceeded: 'Fark bu yöntem için şirket eşiğini aşıyor.',
  unmeasured: 'Ölçülmedi: {{tanks}} (hesaplanan ROB karşılaştırılmadı).',
  adjustment: 'Mutabakat ve Düzeltme',
  reason: 'Fark Nedeni',
  reasons: {
    measurement_tolerance: 'Ölçüm Toleransı (Normal)',
    measurement_error: 'Ölçüm Hatası',
    calibration: 'Kalibrasyon Tablosu',
    density: 'Yoğunluk Farkı',
    temperature: 'Sıcaklık Düzeltmesi',
    unrecorded_consumption: 'Kaydedilmemiş Tüketim',
    sludge_water: 'Çamur / Su Drenajı',
    bunker_difference: 'Yakıt Alımı Farkı',
    other: 'Diğer',
  },
  description: 'Açıklama',
  adjustmentMt: 'Düzeltme Miktarı (MT)',
  document: 'Dayanak Belge',
  byTank: 'Tank bazında düzeltme',
  useDifferences: 'Farkları kullan',
  tankDiff: 'Fark (MT)',
  tankAdjustment: 'Düzeltme (MT)',
  note: 'Not:',
  noteBody:
    'Yapılan ölçüm, sistemdeki stok bakiyesini otomatik olarak değiştirmez. Fark olması durumunda düzeltme talebi oluşturulmalı; talep gemide ve ofiste onaylandıktan sonra deftere işlenir.',
  save: 'Ölçümü Kaydet',
  saved: 'Ölçüm kaydedildi. Stok bakiyesi değişmedi.',
  remeasure: 'Yeniden Ölç',
  request: 'Düzeltme Talep Et',
  requested: 'Düzeltme talebi taslak olarak kaydedildi.',
  submit: 'Onaya Gönder',
  adjustmentRecord: 'Düzeltme talebi {{no}}',
  officeStep: 'Ofis onayı bekleniyor (şirket onaylayıcısı).',
  appliedOnBoard:
    'Onaylandı. Gemi sunucusu senkrondan sonra düzeltmeyi deftere kendiliğinden işler.',
  posted: 'Düzeltme deftere işlendi: {{mt}} MT.',
  approveOffice: 'Onayla',
  returnOffice: 'İade Et',
  officeReadOnly:
    'Ölçüm ve düzeltme talebi gemi verisidir. Ofis düzeltmenin ofis adımına karar verir ve mutabakat eşiklerini belirler.',
  thresholds: 'Mutabakat Eşikleri',
  thresholdsTitle: 'Mutabakat eşikleri ve maliyet yöntemi',
  thresholdsHint:
    'Ölçüm yöntemi başına şirket parametresi: MT ya da % sınırını aşan fark işaretlenir. Boş bırakılırsa eşik yoktur (sonraki aşamada MRV parametre setlerine taşınır).',
  thresholdMt: '{{method}} sınırı (MT)',
  thresholdPct: '{{method}} sınırı (%)',
  costMethod: 'Maliyet yöntemi',
  costMethods: { weighted_average: 'Ağırlıklı Ortalama', batch: 'Parti (BDN)' },
  settingsSaved: 'Eşikler kaydedildi.',
  cancel: 'Vazgeç',
  saveSettings: 'Kaydet',
  status: { within: 'Eşik içinde', exceeds: 'Eşiği aşıyor', undefined: 'Eşik yok' },
  month: {
    title: 'ROB Ay Kapanışı',
    month: 'Ay (UTC)',
    closed: '{{at}} tarihinde {{by}} kapattı.',
    open: 'Açık',
    notEnded: 'Ay henüz bitmedi.',
    noBlockers: 'Engelleyen sorun yok: ay kapatılabilir.',
    close: 'Ayı Kapat',
    closedDone: '{{month}} ayı kapatıldı.',
    remarks: 'Açıklama',
    lockNote: 'Ay gemide kapandıktan sonra dönemi ofis kilitler.',
    blockers: {
      NEGATIVE_ROB: '{{tank}} tankında negatif ROB ({{at}})',
      CAPACITY_EXCEEDED: '{{tank}} tankında kapasite aşımı ({{at}})',
      MISSING_CONVERSION_BASIS: 'Dönüşüm dayanağı olmayan ölçüm: {{tank}} ({{at}})',
      UNAPPROVED_ADJUSTMENT: 'Onaylanmamış düzeltme talebi: {{no}}',
      UNMATCHED_CONSUMPTION: 'Seferi olmayan tüketim ({{at}})',
    },
  },
  report: {
    title: 'ROB Mutabakat Raporu',
    vessel: 'Gemi',
    voyage: 'Sefer',
    period: 'Dönem',
    printed: 'Basım',
    product: 'Ürün',
    opening: 'Açılış ROB (MT)',
    bunkered: 'Toplam Alım (MT)',
    consumed: 'Toplam Tüketim (MT)',
    calculated: 'Hesaplanan ROB (MT)',
    measured: 'Ullage ROB (MT)',
    diff: 'Fark (MT)',
    sounding: 'Son ölçüm {{no}} · {{at}}',
    tankTitle: 'Tank ölçümleri',
    noSounding: 'Dönemde ölçüm yok: ölçülen ROB boş.',
    footer: 'Felixmate · onaylı defter hareketlerinden türetildi',
  },
};

export const fuelVoyageAnalysisEn = {
  title: 'Voyage Analysis',
  views: { operations: 'Operations and Cost', mrv: 'MRV and ETS' },
  mrvSoon:
    'MRV and ETS are not in use: MRV has been deferred (yachts under 5000 GT are outside its scope). The operations and cost analysis above uses the same fuel records.',
  vessel: 'Vessel',
  voyage: 'Voyage',
  leg: 'Leg',
  legOption: 'Leg {{no}}',
  cp: 'Charter Party',
  dateRange: 'Date Range',
  from: 'From',
  to: 'To',
  operation: 'Operation',
  fuel: 'Fuel',
  status: 'Data Status',
  statuses: { approved: 'Approved', all: 'All (incl. provisional)' },
  all: 'All',
  show: 'Show Analysis',
  excel: 'Excel',
  pdf: 'PDF',
  scope: 'Scope: Operations and cost · {{vessel}} · {{range}}',
  scopeVoyage: 'Scope: Operations and cost · {{vessel}} · {{voyage}} · {{range}}',
  provisional: 'Provisional total',
  provisionalNote:
    'Includes draft and reopened voyage reports (provisional). The ROB cards always come from the approved ledger.',
  cards: {
    startRob: 'Starting ROB',
    bunkered: 'Bunkered',
    consumed: 'Consumption',
    endRob: 'Ending ROB',
  },
  table: 'Consumption by Operational Period',
  columns: {
    period: 'Period',
    operation: 'Operation',
    cp: 'CP Coverage',
    total: 'Total (MT)',
  },
  periods: {
    sea: 'At Sea',
    manoeuvring: 'Manoeuvring',
    port: 'In Port',
    anchor: 'At Anchor',
    drift: 'Drifting',
    canal_strait: 'Canal / Strait',
    waiting: 'Waiting',
    out: 'Outside CP',
    partial: 'CP boundary',
  },
  operations: {
    sea: 'Sea Passage',
    manoeuvring: 'Manoeuvring',
    port: 'Port',
    anchor: 'Anchor',
    drift: 'Drift',
    canal_strait: 'Canal / Strait',
    waiting: 'Waiting',
    other: 'Other',
  },
  coverage: { in: 'Within CP', out: 'Outside CP', partial: 'CP boundary', none: 'No CP' },
  totalRow: 'Total',
  empty: 'No posted consumption in this selection.',
  chart: 'Fuel Consumption by Operational Status',
  unit: 'Unit',
  units: { mt: 'MT', pct: '%' },
  chartAxis: 'Consumption ({{unit}})',
  performance: 'Performance Indicators',
  daily: 'Daily Consumption',
  perDay: 'MT/day',
  perNm: 'Consumption per Mile',
  perNmUnit: 'MT/nm',
  distanceBasis: 'Distance Basis',
  distanceBases: { sog: 'SOG (over ground)', stw: 'STW (through water)' },
  cost: 'Fuel Cost',
  costMethod: 'Calculation Method',
  costMethods: { weighted_average: 'Weighted Average', batch: 'Batch (BDN)' },
  costMissing: 'Price missing for {{fuels}}',
  missing: 'Missing Data',
  records_one: '{{count}} record',
  records_other: '{{count}} records',
  sources: 'Source Records',
  sourcesTitle: 'Source records and missing data',
  missingCodes: {
    QUALITY_REVIEW: 'Consumption needs review (quality)',
    UNASSIGNED_VOYAGE: 'Report not matched to a voyage',
    NOT_POSTED: 'Report not submitted (not in the ledger)',
    MISSING_DISTANCE: 'Distance missing for the sea interval',
    MISSING_PRICE: 'No bunker price for {{fuel}}',
  },
  intervalsTitle: 'Intervals in the analysis',
  intervalColumns: {
    report: 'Report',
    period: 'Interval (UTC)',
    operation: 'Operation',
    leg: 'Leg',
    cp: 'CP',
    total: 'MT',
  },
  close: 'Close',
  hoursBasis: 'Over {{hours}} h of consumption intervals',
  distanceBasisHint: '{{nm}} nm (sea intervals only)',
  export: {
    title: 'Voyage Analysis — Operations and Cost',
    generated: 'Generated',
    sheet: 'Operations',
  },
};

export const fuelVoyageAnalysisTr: typeof fuelVoyageAnalysisEn = {
  title: 'Voyage Analizi',
  views: { operations: 'Operasyon ve Maliyet', mrv: 'MRV ve ETS' },
  mrvSoon:
    'MRV ve ETS kullanılmıyor: MRV ertelendi (5000 GT altındaki yatlar kapsam dışı). Operasyon ve maliyet analizi aynı yakıt kayıtlarını kullanır.',
  vessel: 'Gemi',
  voyage: 'Voyage',
  leg: 'Leg',
  legOption: 'Leg {{no}}',
  cp: 'Charter Party',
  dateRange: 'Tarih Aralığı',
  from: 'Başlangıç',
  to: 'Bitiş',
  operation: 'Operasyon',
  fuel: 'Yakıt',
  status: 'Veri Durumu',
  statuses: { approved: 'Onaylı', all: 'Tümü (geçici dahil)' },
  all: 'Tümü',
  show: 'Analizi Göster',
  excel: 'Excel',
  pdf: 'PDF',
  scope: 'Kapsam: Operasyon ve maliyet · {{vessel}} · {{range}}',
  scopeVoyage: 'Kapsam: Operasyon ve maliyet · {{vessel}} · {{voyage}} · {{range}}',
  provisional: 'Geçici toplam',
  provisionalNote:
    'Taslak ve yeniden açılmış sefer raporları dahil (geçici). ROB kartları her zaman onaylı defterden gelir.',
  cards: {
    startRob: 'Başlangıç ROB',
    bunkered: 'Yakıt Alımı',
    consumed: 'Tüketim',
    endRob: 'Bitiş ROB',
  },
  table: 'Operasyonel Dönem Bazında Tüketim',
  columns: {
    period: 'Dönem',
    operation: 'Operasyon',
    cp: 'CP Kapsamı',
    total: 'Toplam (MT)',
  },
  periods: {
    sea: 'Denizde',
    manoeuvring: 'Manevra',
    port: 'Limanda',
    anchor: 'Demirde',
    drift: 'Driftte',
    canal_strait: 'Kanal / Boğaz',
    waiting: 'Beklemede',
    out: 'CP Dışı',
    partial: 'CP Sınırı',
  },
  operations: {
    sea: 'Seyir',
    manoeuvring: 'Manevra',
    port: 'Liman',
    anchor: 'Demirde',
    drift: 'Driftte',
    canal_strait: 'Kanal / Boğaz',
    waiting: 'Bekleme',
    other: 'Diğer',
  },
  coverage: { in: 'CP Kapsamında', out: 'CP Dışı', partial: 'CP Sınırı', none: 'CP Yok' },
  totalRow: 'Toplam',
  empty: 'Bu seçimde işlenmiş tüketim yok.',
  chart: 'Operasyonel Duruma Göre Yakıt Tüketimi',
  unit: 'Birim',
  units: { mt: 'MT', pct: '%' },
  chartAxis: 'Tüketim ({{unit}})',
  performance: 'Performans Göstergeleri',
  daily: 'Günlük Tüketim',
  perDay: 'MT/gün',
  perNm: 'Mil Başına Tüketim',
  perNmUnit: 'MT/nm',
  distanceBasis: 'Mesafe Bazı',
  distanceBases: { sog: 'SOG (yere göre)', stw: 'STW (suya göre)' },
  cost: 'Yakıt Maliyeti',
  costMethod: 'Hesaplama Metodu',
  costMethods: { weighted_average: 'Ağırlıklı Ortalama', batch: 'Parti (BDN)' },
  costMissing: '{{fuels}} için fiyat yok',
  missing: 'Eksik Veri',
  records_one: '{{count}} kayıt',
  records_other: '{{count}} kayıt',
  sources: 'Kaynak Kayıtlar',
  sourcesTitle: 'Kaynak kayıtlar ve eksik veri',
  missingCodes: {
    QUALITY_REVIEW: 'Tüketim incelemesi gerekli (kalite)',
    UNASSIGNED_VOYAGE: 'Rapor bir sefere bağlı değil',
    NOT_POSTED: 'Rapor gönderilmedi (defterde yok)',
    MISSING_DISTANCE: 'Seyir aralığında mesafe yok',
    MISSING_PRICE: '{{fuel}} için alım fiyatı yok',
  },
  intervalsTitle: 'Analize giren aralıklar',
  intervalColumns: {
    report: 'Rapor',
    period: 'Aralık (UTC)',
    operation: 'Operasyon',
    leg: 'Leg',
    cp: 'CP',
    total: 'MT',
  },
  close: 'Kapat',
  hoursBasis: '{{hours}} saatlik tüketim aralığı üzerinden',
  distanceBasisHint: '{{nm}} nm (yalnız seyir aralıkları)',
  export: {
    title: 'Voyage Analizi — Operasyon ve Maliyet',
    generated: 'Oluşturulma',
    sheet: 'Operasyon',
  },
};

export const fuelReconciliationErrorsEn = {
  INVALID_SOUNDING: 'Some measurement fields are not valid.',
  SOUNDING_INCOMPLETE: 'Fill in the required measurement fields.',
  DENSITY_OUT_OF_RANGE: 'The density is outside ASTM Table 54B (0.6105–1.1635 t/m³).',
  CLOSE_BLOCKED: 'The month cannot be closed: resolve the listed issues first.',
  MONTH_NOT_ENDED: 'The month has not ended yet.',
  MONTH_ALREADY_CLOSED: 'This month is already closed.',
  INVALID_FUEL_SETTINGS: 'Some threshold values are not valid.',
};

export const fuelReconciliationErrorsTr: typeof fuelReconciliationErrorsEn = {
  INVALID_SOUNDING: 'Bazı ölçüm alanları geçersiz.',
  SOUNDING_INCOMPLETE: 'Zorunlu ölçüm alanlarını doldurun.',
  DENSITY_OUT_OF_RANGE: 'Yoğunluk ASTM Tablo 54B aralığının dışında (0,6105–1,1635 t/m³).',
  CLOSE_BLOCKED: 'Ay kapatılamıyor: önce listelenen sorunları giderin.',
  MONTH_NOT_ENDED: 'Ay henüz bitmedi.',
  MONTH_ALREADY_CLOSED: 'Bu ay zaten kapatıldı.',
  INVALID_FUEL_SETTINGS: 'Bazı eşik değerleri geçersiz.',
};
